import { useOrganization } from "@/contexts/OrganizationContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Building2, ChevronDown, Check, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

export const OrganizationSwitcher = ({ className }: { className?: string }) => {
  const { t } = useLanguage();
  const pm = t.pm || {} as any;
  
  const roleLabels: Record<string, string> = {
    owner: pm.owner || "Owner",
    admin: pm.admin || "Admin",
    dispatcher: pm.dispatcher || "Dispatcher",
    finance: pm.finance || "Finance",
    readonly: pm.readonly || "Read Only",
  };

  const {
    organizations,
    currentOrganization,
    currentRole,
    switchOrganization,
    loading,
  } = useOrganization();

  if (loading || !currentOrganization) {
    return (
      <div className={cn("flex items-center gap-2 px-3 py-2", className)}>
        <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse" />
        <div className="flex-1">
          <div className="h-4 w-24 bg-white/10 rounded animate-pulse" />
          <div className="h-3 w-16 bg-white/10 rounded mt-1 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-2 px-3 py-2 h-auto text-left",
            className
          )}
        >
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={currentOrganization.logo_url || undefined} />
            <AvatarFallback className="rounded-lg bg-white/20 text-white">
              {currentOrganization.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate text-white">
              {currentOrganization.name}
            </p>
            <p className="text-xs text-blue-200 truncate">
              {roleLabels[currentRole || "readonly"]}
            </p>
          </div>
          <ChevronDown className="w-4 h-4 text-blue-300 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{pm.organizations || "Organizations"}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => switchOrganization(org.id)}
            className="flex items-center gap-2 py-2"
          >
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={org.logo_url || undefined} />
              <AvatarFallback className="rounded-lg bg-blue-100 text-blue-600">
                {org.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{org.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {org.city || org.slug}
              </p>
            </div>
            {org.id === currentOrganization.id && (
              <Check className="w-4 h-4 text-blue-600 shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/pm/settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            {pm.organizationSettings || "Organization Settings"}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
